import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { addTermToVocabulary } from '@src/utility/customVocabulary';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback } from 'react';

const logger = LogManager.getLogger('feature:CompanyDictionary');

/**
 * Adds terms to the Brand Kit "Company Dictionary" (`customVocabulary`) so the
 * backend transcription keyterms prompt picks up the corrected spelling next
 * time. Follows the comma-separated contract in
 * src/utility/customVocabulary.ts.
 */
export const useCompanyDictionary = () => {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();

  const addTerm = useCallback(
    async (term: string) => {
      const organization = videoCreator.organization;
      if (!organization?.branding) {
        logger.warn(
          'Cannot add term to Company Dictionary: no organization branding',
        );
        return;
      }

      const current = organization.branding.customVocabulary || '';
      const updated = addTermToVocabulary(current, term);
      if (updated === current) return;

      try {
        organization.branding = {
          ...organization.branding,
          customVocabulary: updated,
        };
        await datoClientStore.albumRepository?.update(organization);
      } catch (err) {
        logger.error('Failed to add term to Company Dictionary', {
          term,
          ...extractErrorInfo(err),
        });
      }
    },
    [videoCreator, datoClientStore],
  );

  return { addTerm };
};
